import { UseFormReturnType, isNotEmpty, useForm } from '@mantine/form'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'

import {
  AppointmentsByPersonRecordDto,
  CreateAppointmentDto,
  CreateAppointmentDtoV2,
} from 'src/api/generated'
import dayjs from 'src/common/libs/dayjs'
import { PHONE_NUMBER_FOR_APPOINTMENTS } from 'src/default/config'

import {
  useCreateAppointmentMutation,
  useUpdateAppointmentMutation,
} from '../api/appointment.api'
import {
  AppointmentStatus,
  IAppointmentFormData,
  IUpdateAppointment,
} from '../appointment.type'
import {
  openManageAppointmentModal,
  openSuccessModal,
} from '../components/ManageAppointmentModal/openAppointmentModals'

export interface IManageAppointmentFormUseCase {
  form: UseFormReturnType<IAppointmentFormData>
  onSubmit: () => void
  isLoading: boolean
}

export const manageAppointmentFormUseCase = (
  personId: string,
  initialData?: AppointmentsByPersonRecordDto,
): IManageAppointmentFormUseCase => {
  const [createAppointment, { isLoading: isCreating }] =
    useCreateAppointmentMutation()
  const [updateAppointment, { isLoading: isUpdating }] =
    useUpdateAppointmentMutation()

  const form = useForm<IAppointmentFormData>({
    mode: 'uncontrolled',
    initialValues: {
      id: initialData?.id,
      personId,
      benefitType: initialData?.benefitType || '',
      appointmentDate: initialData?.appointmentDate
        ? new Date(initialData.appointmentDate)
        : '',
      repPhoneNumber: PHONE_NUMBER_FOR_APPOINTMENTS,
      clinicInformation: initialData?.clinicInformation || '',
      clinicZipCode: initialData?.clinicZipCode || '',
      preferredLanguage: initialData?.preferredLanguage || '',
      status: initialData?.status || '',
      recommendations: initialData?.recommendations || '',
    },
    validateInputOnBlur: true,
    validate: {
      benefitType: isNotEmpty('Benefit Type is required'),
      appointmentDate: isNotEmpty('Appointment Date is required'),
      repPhoneNumber: isNotEmpty('Representative Phone Number is required'),
      clinicInformation: isNotEmpty('Clinic Information is required'),
      clinicZipCode: isNotEmpty('Clinic Zip Code is required'),
      preferredLanguage: isNotEmpty('Preferred Language is required'),
      status: isNotEmpty('Status is required'),
    },
  })

  const onSubmit = () => {
    form.onSubmit(async (values: IAppointmentFormData) => {
      try {
        const formattedDate = dayjs.tz(values.appointmentDate).format()
        let result

        const appointmentData = {
          personId: values.personId,
          appointmentDate: formattedDate,
          status: values.status as AppointmentStatus,
          repPhoneNumber: values.repPhoneNumber,
          clinicInformation: values.clinicInformation,
          clinicZipCode: values.clinicZipCode,
          benefitType: values.benefitType,
          preferredLanguage: values.preferredLanguage,
          recommendations: values.recommendations,
        }

        if (values.id) {
          result = await updateAppointment({
            id: values.id,
            ...appointmentData,
          } as IUpdateAppointment).unwrap()
        } else {
          result = await createAppointment(
            appointmentData as CreateAppointmentDtoV2,
          ).unwrap()
        }

        modals.closeAll()

        openSuccessModal(
          { ...values, id: result.id } as AppointmentsByPersonRecordDto,
          () => {
            modals.closeAll()
            form.reset()
            openManageAppointmentModal(personId)
          },
          () => {
            modals.closeAll()
            openManageAppointmentModal(personId, {
              ...values,
              id: result.id,
              appointmentDate: new Date(result.appointmentDate ?? ''),
            } as any)
          },
        )
      } catch (error) {
        notifications.show({
          title: 'Error',
          message: 'There was an error!',
          color: 'red',
        })
      }
    })()
  }

  return {
    form,
    onSubmit,
    isLoading: isCreating || isUpdating,
  }
}
